import React, { useState, useEffect, useCallback, useRef } from 'react';
import { hoyEnChile } from '../utils/dateHelpers';
import { Globe, Building2, MapPin, Check, ChevronRight, ChevronLeft, Megaphone } from 'lucide-react';
import { CampanaMaster } from '../lib/supabase';
import { campanasMasterService, DistribucionLumisial, CrearCampanaMasterPayload } from '../services/campanasMasterService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useAuth } from '../context/AuthContext';
import { getZonalAccess, puedeAdministrarMaestra } from '../utils/permissions';
import { formatCurrency } from '../utils/formatters';
import { NIVEL_LABELS } from '../utils/campanaConstants';
import { recalcularDistribucion } from '../utils/distribucionCampana';
import { FullScreenShell } from './ui/FullScreenShell';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { FORM_BORDER, FORM_BORDER_HOVER, FOCUS_RING, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

type Paso = 1 | 2 | 3;

const MODO_INFO: Record<string, { label: string; desc: string }> = {
  fija: {
    label: 'Fija',
    desc: 'Se define un objetivo global que se distribuye entre las sedes. Puedes repartirlo en partes iguales o asignar cada monto manualmente.',
  },
  libre: {
    label: 'Libre',
    desc: 'Cada sede define su propio objetivo individual. No se establece un monto global.',
  },
};

const ROLE_TO_NIVEL: Record<string, CampanaMaster['nivel_origen']> = {
  admin_global: 'global',
  user_global: 'global',
  admin_nacional: 'nacional',
  user_nacional: 'nacional',
};

const pasoIndicadorBg = (n: number, paso: Paso) => {
  if (paso > n) return 'bg-emerald-500 text-white';
  if (paso === n) return 'bg-slate-800 text-white';
  return 'bg-slate-200 text-slate-500';
};

const colorDiferencia = (diferencia: number): string => {
  if (diferencia === 0) return 'text-emerald-600';
  return diferencia > 0 ? 'text-amber-600' : 'text-red-600';
};

const PasoIndicador: React.FC<{ readonly n: number; readonly label: string; readonly paso: Paso }> = ({ n, label, paso }) => (
  <div className="flex items-center gap-2">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${pasoIndicadorBg(n, paso)}`}>
      {paso > n ? <Check className="w-4 h-4" /> : n}
    </div>
    <span className={`text-sm font-medium ${paso === n ? 'text-slate-800' : 'text-slate-400'}`}>{label}</span>
  </div>
);

export const CampanaMasterWizardModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const { usuario } = useAuth();
  const { error, handleError, clearError } = useErrorHandler();
  const [paso, setPaso] = useState<Paso>(1);
  const [submitting, setSubmitting] = useState(false);
  const [creacionPendiente, setCreacionPendiente] = useState(false);
  const campanaMasterIdRef = useRef<string | null>(null);
  const payloadPendienteRef = useRef<CrearCampanaMasterPayload | null>(null);
  const [loading, setLoading] = useState(false);

  const zonalAccess = getZonalAccess(usuario);
  const rolNombre = usuario?.rol?.nombre || '';
  const nivelOrigen: CampanaMaster['nivel_origen'] = ROLE_TO_NIVEL[rolNombre] ?? 'diocesano';
  const puedeCrear = puedeAdministrarMaestra(usuario, { nivel_origen: nivelOrigen,
    diocesis_id: usuario?.diocesis_id ?? undefined,
    provincia_eclesiastica_id: usuario?.provincia_eclesiastica_id ?? undefined });

  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    objetivo_global: '',
    modo_distribucion: 'libre' as CampanaMaster['modo_distribucion'],
    fecha_inicio: hoyEnChile(),
    fecha_fin: '',
  });
  const [distribucionAutomatica, setDistribucionAutomatica] = useState(true);
  const checkboxAllRef = useRef<HTMLInputElement>(null);

  const [distribucion, setDistribucion] = useState<DistribucionLumisial[]>([]);

  const cargarLumisiales = useCallback(async () => {
    setLoading(true);
    try {
      const lista = await campanasMasterService.getLumisialesElegibles({
        provinciaEclesiasticaId: zonalAccess.provinciaEclesiasticaId || undefined,
        diocesisId: zonalAccess.diocesisId || undefined,
      });
      setDistribucion(lista.map(l => ({ ...l, seleccionado: false, montoAsignado: 0 })));
    } catch (e) {
      reportarErrorInesperado(e, 'No pudimos cargar las sedes disponibles.');
    } finally {
      setLoading(false);
    }
  }, [zonalAccess.provinciaEclesiasticaId, zonalAccess.diocesisId]);

  useEffect(() => {
    if (isOpen) {
      if (payloadPendienteRef.current) return;
      campanaMasterIdRef.current = crypto.randomUUID();
      setPaso(1);
      setForm({
        nombre: '',
        descripcion: '',
        objetivo_global: '',
        modo_distribucion: 'libre',
        fecha_inicio: hoyEnChile(),
        fecha_fin: '',
      });
      clearError();
      cargarLumisiales();
    }
  }, [isOpen, cargarLumisiales, clearError]);

  const handleBeforeClose = () => !submitting && !creacionPendiente;

  const objetivo = Number.parseFloat(form.objetivo_global) || 0;
  const seleccionados = distribucion.filter(l => l.seleccionado);
  const sumaAsignada = seleccionados.reduce((s, l) => s + l.montoAsignado, 0);
  const diferencia = objetivo - sumaAsignada;

  const toggleSeleccion = (lumisialId: string) => {
    const nuevo = distribucion.map(l =>
      l.lumisialId === lumisialId ? { ...l, seleccionado: !l.seleccionado } : l
    );
    setDistribucion(recalcularDistribucion(nuevo, form.modo_distribucion, objetivo, distribucionAutomatica));
  };

  const toggleTodos = () => {
    const todosSeleccionados = distribucion.every(l => l.seleccionado);
    const nuevo = distribucion.map(l => ({ ...l, seleccionado: !todosSeleccionados }));
    setDistribucion(recalcularDistribucion(nuevo, form.modo_distribucion, objetivo, distribucionAutomatica));
  };

  useEffect(() => {
    if (!checkboxAllRef.current) return;
    const total = distribucion.length;
    const sel = distribucion.filter(l => l.seleccionado).length;
    checkboxAllRef.current.checked = total > 0 && sel === total;
    checkboxAllRef.current.indeterminate = sel > 0 && sel < total;
  }, [distribucion]);

  const cambiarMonto = (lumisialId: string, monto: number) => {
    setDistribucion(prev => prev.map(l =>
      l.lumisialId === lumisialId ? { ...l, montoAsignado: monto } : l
    ));
  };

  const handleModoChange = (modo: CampanaMaster['modo_distribucion']) => {
    setForm(f => ({ ...f, modo_distribucion: modo }));
    setDistribucion(prev => recalcularDistribucion(prev, modo, objetivo, distribucionAutomatica));
  };

  const handleDistribucionAutomaticaChange = (auto: boolean) => {
    setDistribucionAutomatica(auto);
    if (auto) {
      setDistribucion(prev => recalcularDistribucion(prev, form.modo_distribucion, objetivo, true));
    }
  };

  const validarPaso1 = () => {
    if (!form.nombre.trim()) return 'El nombre es requerido.';
    if (form.fecha_fin && form.fecha_fin < form.fecha_inicio)
      return 'La fecha de fin no puede ser anterior a la de inicio.';
    return null;
  };

  const validarPaso2 = () => {
    if (seleccionados.length === 0) return 'Debes seleccionar al menos una sede.';
    if (seleccionados.some(l => !Number.isFinite(l.montoAsignado) || l.montoAsignado < 0
      || Math.abs(l.montoAsignado * 100 - Math.round(l.montoAsignado * 100)) > 0.000001)) {
      return 'Cada monto debe ser válido, no negativo y tener como máximo dos decimales.';
    }
    if (form.modo_distribucion === 'fija') {
      if (!form.objetivo_global || objetivo <= 0)
        return 'El objetivo global es requerido para el tipo de meta Fija.';
      const sumaCentavos = seleccionados.reduce((s, l) => s + Math.round(l.montoAsignado * 100), 0);
      if (sumaCentavos !== Math.round(objetivo * 100))
        return `La suma asignada (${formatCurrency(sumaAsignada)}) no coincide con el objetivo global (${formatCurrency(objetivo)}).`;
    }
    return null;
  };

  const avanzar = () => {
    clearError();
    if (paso === 1) {
      const err = validarPaso1();
      if (err) { handleError(err); return; }
    }
    if (paso === 2) {
      const err = validarPaso2();
      if (err) { handleError(err); return; }
    }
    setPaso(p => (p + 1) as Paso);
  };

  const retroceder = () => {
    clearError();
    setPaso(p => (p - 1) as Paso);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearError();
    if (!usuario || !puedeCrear) {
      handleError('No hay usuario autenticado. Por favor recarga la página.');
      setSubmitting(false);
      return;
    }
    try {
      const payload: CrearCampanaMasterPayload = payloadPendienteRef.current ?? {
        campanaMasterId: campanaMasterIdRef.current ?? crypto.randomUUID(),
        campana: {
          nombre: form.nombre.trim(),
          descripcion: form.descripcion.trim() || undefined,
          objetivo_global: objetivo,
          modo_distribucion: form.modo_distribucion,
          fecha_inicio: form.fecha_inicio,
          fecha_fin: form.fecha_fin || undefined,
          estado: 'planificacion',
          permite_editar_objetivo_local: form.modo_distribucion === 'libre',
          nivel_origen: nivelOrigen,
          provincia_eclesiastica_id: zonalAccess.provinciaEclesiasticaId || undefined,
          diocesis_id: zonalAccess.diocesisId || undefined,
        },
        distribucion: seleccionados.map(l => ({
          lumisialId: l.lumisialId,
          monto: l.montoAsignado,
        })),
      };
      campanaMasterIdRef.current = payload.campanaMasterId;
      payloadPendienteRef.current = payload;
      setCreacionPendiente(true);
      await campanasMasterService.crear(payload);
    } catch (e) {
      const rechazoConfirmado = typeof e === 'object' && e !== null && 'code' in e
        && typeof e.code === 'string' && /^(?:[0-9A-Z]{5}|PGRST\d+)$/.test(e.code);
      if (rechazoConfirmado) {
        payloadPendienteRef.current = null;
        setCreacionPendiente(false);
        handleError('No se pudo distribuir la campaña. Revisa sus datos y tu permiso para esa entidad.');
      } else {
        handleError('El resultado está pendiente de verificar. Reintenta para consultar la misma campaña, sin crear otra.');
      }
      reportarErrorInesperado(e, 'No pudimos completar la distribución de la campaña.');
      setSubmitting(false);
      return;
    }
    payloadPendienteRef.current = null;
    campanaMasterIdRef.current = null;
    setCreacionPendiente(false);
    setSubmitting(false);
    onSuccess();
    onClose();
  };

  const diferenciaColor = colorDiferencia(diferencia);

  const header = (
    <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-sky-100 p-2 rounded-lg">
            <Megaphone className="w-6 h-6 text-sky-600" />
          </div>
          <div>
            <h2 id="campana-wizard-title" className="text-xl font-bold text-slate-800">Distribuir Campaña</h2>
            <p className="text-sm text-slate-500">Nivel: {NIVEL_LABELS[nivelOrigen]}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <PasoIndicador n={1} label="Datos generales" paso={paso} />
          <div className="flex-1 h-px bg-slate-200" />
          <PasoIndicador n={2} label="Distribución" paso={paso} />
          <div className="flex-1 h-px bg-slate-200" />
          <PasoIndicador n={3} label="Confirmación" paso={paso} />
        </div>
      </div>
    </div>
  );

  return (
    <FullScreenShell
      isOpen={isOpen}
      onClose={onClose}
      header={header}
      variant="centered"
      onBeforeClose={handleBeforeClose}
    >
      <fieldset disabled={submitting || creacionPendiente || !puedeCrear} className="p-6">
          {!puedeCrear && <p>La creación corresponde a la junta responsable de este nivel.</p>}
          <FormErrorAlert error={error} onClear={clearError} spacingClassName="mb-4" />

          {paso === 1 && (
            <div className="space-y-6">
              <TextInput
                id="nombre-campana"
                label="Nombre de la Campaña *"
                value={form.nombre}
                onChange={v => setForm(f => ({ ...f, nombre: v }))}
                placeholder="Ej: Casa de Retiro Nacional 2026"
              />
              <Textarea
                id="descripcion-campana"
                label="Descripción"
                value={form.descripcion}
                onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                rows={3}
                placeholder="Describe el propósito de la campaña..."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fecha-inicio" className="block text-sm font-medium text-slate-600 mb-1.5">Fecha de Inicio *</label>
                  <input
                    id="fecha-inicio"
                    type="date"
                    value={form.fecha_inicio}
                    onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))}
                    className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                  />
                </div>
                <div>
                  <label htmlFor="fecha-fin" className="block text-sm font-medium text-slate-600 mb-1.5">Fecha de Fin (Opcional)</label>
                  <input
                    id="fecha-fin"
                    type="date"
                    value={form.fecha_fin}
                    onChange={e => setForm(f => ({ ...f, fecha_fin: e.target.value }))}
                    className={`w-full px-4 py-3 ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} ${FORM_RADIUS} bg-white`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Tipo de Meta *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(Object.keys(MODO_INFO) as Array<CampanaMaster['modo_distribucion']>).map(modo => (
                    <button
                      key={modo}
                      type="button"
                      onClick={() => handleModoChange(modo)}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        form.modo_distribucion === modo
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="font-semibold text-slate-800 mb-1">{MODO_INFO[modo].label}</p>
                      <p className="text-xs text-slate-500">{MODO_INFO[modo].desc}</p>
                    </button>
                  ))}
                </div>
              </div>

            </div>
          )}

          {paso === 2 && (
            <div className="space-y-4">
              {form.modo_distribucion === 'fija' && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <TextInput
                        id="objetivo-global"
                        label="Objetivo Global ($) *"
                        type="number"
                        value={form.objetivo_global}
                        onChange={v => {
                          setForm(f => ({ ...f, objetivo_global: v }));
                          const obj = Number.parseFloat(v) || 0;
                          setDistribucion(prev => recalcularDistribucion(prev, form.modo_distribucion, obj, distribucionAutomatica));
                        }}
                        min={0}
                        step={0.01}
                        placeholder="0"
                        prefixIcon={<span className="text-slate-500 text-sm">$</span>}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-700 mb-1">Distribución de montos</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleDistribucionAutomaticaChange(true)}
                          className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            distribucionAutomatica ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          Igual automático
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDistribucionAutomaticaChange(false)}
                          className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                            !distribucionAutomatica ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          Manual
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Objetivo global</p>
                      <p className="font-bold text-slate-800">{formatCurrency(objetivo)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Suma asignada</p>
                      <p className="font-bold text-slate-800">{formatCurrency(sumaAsignada)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Diferencia</p>
                      <p className={`font-bold ${diferenciaColor}`}>
                        {diferencia === 0 ? '✓ Cuadra' : formatCurrency(Math.abs(diferencia))}
                        {(() => {
                          if (diferencia > 0) return ' pendiente';
                          if (diferencia < 0) return ' excedido';
                          return '';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold">{seleccionados.length}</span> de{' '}
                    <span className="font-semibold">{distribucion.length}</span> sedes seleccionadas
                  </p>

                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="w-10 p-3 text-left">
                            <input
                              ref={checkboxAllRef}
                              type="checkbox"
                              onChange={toggleTodos}
                              aria-label="Seleccionar todas las sedes"
                              className="w-4 h-4 accent-sky-600 cursor-pointer"
                            />
                          </th>
                          <th className="p-3 text-left font-semibold text-slate-600">Sede</th>
                          <th className="p-3 text-left font-semibold text-slate-600 hidden md:table-cell">Diócesis</th>
                          {form.modo_distribucion === 'fija' && (
                            <th className="p-3 text-right font-semibold text-slate-600">Objetivo asignado</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {distribucion.map(l => (
                          <tr
                            key={l.lumisialId}
                            className={`transition-colors cursor-pointer ${l.seleccionado ? 'bg-sky-50' : 'hover:bg-slate-50'}`}
                            onClick={() => toggleSeleccion(l.lumisialId)}
                          >
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={l.seleccionado}
                                onChange={() => toggleSeleccion(l.lumisialId)}
                                onClick={e => e.stopPropagation()}
                                aria-label={`Seleccionar ${l.lumisialNombre}`}
                                className="w-4 h-4 accent-sky-600 cursor-pointer"
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                <div>
                                  <p className="font-medium text-slate-800">{l.lumisialNombre}</p>
                                  <p className="text-xs text-slate-500">{l.lumisialCiudad}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-slate-600 hidden md:table-cell">{l.diocesisNombre}</td>
                            {form.modo_distribucion === 'fija' && (
                              <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                                {l.seleccionado && !distribucionAutomatica ? (
                                  <input
                                    type="number"
                                    value={l.montoAsignado || ''}
                                    onChange={e => cambiarMonto(l.lumisialId, Number.parseFloat(e.target.value) || 0)}
                                    min="0"
                                    step="0.01"
                                    className="w-28 px-2 py-1 border border-slate-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 text-sm"
                                    placeholder="0"
                                  />
                                ) : (
                                  <span className={l.seleccionado ? 'font-medium text-slate-800' : 'text-slate-400'}>
                                    {l.seleccionado ? formatCurrency(l.montoAsignado) : '—'}
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {paso === 3 && (
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                <h3 className="font-bold text-slate-800 text-lg">{form.nombre}</h3>
                {form.descripcion && <p className="text-slate-600 text-sm">{form.descripcion}</p>}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Nivel</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Globe className="w-4 h-4 text-sky-600" />
                      <span className="font-semibold text-slate-800 text-sm">{NIVEL_LABELS[nivelOrigen]}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Sedes</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Building2 className="w-4 h-4 text-sky-600" />
                      <span className="font-semibold text-slate-800 text-sm">{seleccionados.length}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Objetivo global</p>
                    <p className="font-semibold text-slate-800 text-sm mt-1">
                      {form.modo_distribucion === 'fija' ? formatCurrency(objetivo) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Tipo de Meta</p>
                    <p className="font-semibold text-slate-800 text-sm mt-1">{MODO_INFO[form.modo_distribucion]?.label ?? form.modo_distribucion}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    form.modo_distribucion === 'libre'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {form.modo_distribucion === 'libre' ? 'Objetivo editable por sedes' : 'Objetivo fijo'}
                  </span>
                  <span className="px-2 py-1 rounded-full bg-slate-200 text-slate-600 text-xs font-medium">
                    {form.fecha_inicio} {form.fecha_fin ? `→ ${form.fecha_fin}` : '(sin fecha fin)'}
                  </span>
                </div>
              </div>

              {seleccionados.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Sedes incluidas:</p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {seleccionados.map(l => (
                      <div key={l.lumisialId} className="flex items-center justify-between px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-700">{l.lumisialNombre}</span>
                          <span className="text-slate-400">{l.lumisialCiudad}</span>
                        </div>
                        {form.modo_distribucion === 'fija' && (
                          <span className="font-semibold text-slate-800">{formatCurrency(l.montoAsignado)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-sm text-sky-800">
                Se crearán <strong>{seleccionados.length} campaña(s)</strong> locales, una por cada sede seleccionada.
                {form.modo_distribucion === 'fija' && distribucionAutomatica && (
                  <> Los montos se distribuyeron en partes iguales automáticamente.</>
                )}
                {' '}Los secretarios de cada Lumisial podrán ver y gestionar su campaña de forma independiente.
              </div>
            </div>
          )}
        </fieldset>

      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex items-center gap-3">
        {paso > 1 && (
          <button
            type="button"
            onClick={retroceder}
            disabled={submitting || creacionPendiente}
            className="flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          disabled={submitting || creacionPendiente}
          className="px-5 py-3 bg-slate-100 text-slate-600 font-semibold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
        <div className="flex-1" />
        {paso < 3 ? (
          <button
            type="button"
            onClick={avanzar}
            disabled={loading || submitting}
            aria-disabled={loading || submitting}
            className="flex items-center gap-2 px-6 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50"
          >
            Siguiente
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Distribuyendo...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {creacionPendiente ? 'Verificar distribución' : 'Distribuir campaña'}
              </>
            )}
          </button>
        )}
      </div>
    </FullScreenShell>
  );
};
